import React, { useState } from 'react';
import {
	TextField,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Box,
} from '@mui/material';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const defaultValue = [
	{
		day: daysOfWeek[0],
		timeRanges: [{ startTime: null, endTime: null }],
	},
	{
		day: daysOfWeek[1],
		timeRanges: [{ startTime: null, endTime: null }],
	},
	{
		day: daysOfWeek[2],
		timeRanges: [{ startTime: null, endTime: null }],
	},
	{
		day: daysOfWeek[3],
		timeRanges: [{ startTime: null, endTime: null }],
	},
	{
		day: daysOfWeek[4],
		timeRanges: [{ startTime: null, endTime: null }],
	},
	{
		day: daysOfWeek[5],
		timeRanges: [{ startTime: null, endTime: null }],
	},
	{
		day: daysOfWeek[6],
		timeRanges: [{ startTime: null, endTime: null }],
	},
];

// Normalize the data structure to support both old and new formats
const normalizeSchedule = (schedule) => {
	if (!schedule || schedule.length !== 7) return defaultValue;

	return schedule.map((daySchedule) => {
		// If old format (startTime/endTime), convert to new format
		if (daySchedule.startTime !== undefined && daySchedule.endTime !== undefined && !daySchedule.timeRanges) {
			return {
				day: daySchedule.day,
				timeRanges: [{ startTime: daySchedule.startTime, endTime: daySchedule.endTime }],
			};
		}
		// If new format, ensure timeRanges exists and has at least one entry
		return {
			day: daySchedule.day,
			timeRanges: daySchedule.timeRanges && daySchedule.timeRanges.length > 0
				? daySchedule.timeRanges
				: [{ startTime: null, endTime: null }],
		};
	});
};

const Schedule = ({ name, value, onChange }) => {
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedDay, setSelectedDay] = useState(null);
	const [selectedRange, setSelectedRange] = useState(null);
	const [scheduleToUse, setScheduleToUse] = useState(normalizeSchedule(value));

	const handleTimeChange = (time, type) => {
		const updatedSchedule = [...scheduleToUse];
		const formattedMinutes = time.getMinutes() < 10 ? '0' + time.getMinutes() : time.getMinutes();
		if (isNaN(formattedMinutes)) {
			updatedSchedule[selectedDay].timeRanges[selectedRange][type] = null;
			onChange({ target: { name, value: updatedSchedule } });
			return;
		}

		let newTime = time.getHours() + ':' + formattedMinutes;

		// Add seconds to end times to minimize gaps (e.g., 23:59 becomes 23:59:59)
		if (type === 'endTime') {
			newTime += ':59';
		} else {
			// Start times get :00 seconds
			newTime += ':00';
		}

		// Check if endTime is smaller than startTime
		if (type === 'endTime') {
			const startTime = updatedSchedule[selectedDay].timeRanges[selectedRange].startTime;
			const [startHours, startMinutes] = startTime.split(':').map(Number);
			const startInMinutes = startHours * 60 + startMinutes;

			const [endHours, endMinutes] = newTime.split(':').map(Number);
			const endInMinutes = endHours * 60 + endMinutes;
			if (endInMinutes < startInMinutes) {
				updatedSchedule[selectedDay].timeRanges[selectedRange].endTime = null;
				onChange({ target: { name, value: updatedSchedule } });
				return;
			}
		}
		updatedSchedule[selectedDay].timeRanges[selectedRange][type] = newTime;
		onChange({ target: { name, value: updatedSchedule } });
	};

	const handleOpenDialog = (dayIndex, rangeIndex) => {
		setSelectedDay(dayIndex);
		setSelectedRange(rangeIndex);
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setSelectedDay(null);
	};

	// This adds another from/until combo to the table.
	const handleAddExtraScheduleLine = () => {
		// loop over the days in the schedule and add another timeRange.
		setScheduleToUse(previousState => {
			const updatedSchedule = previousState.map(previousStateDay => {
				return {
					...previousStateDay,
					timeRanges: [...previousStateDay.timeRanges, { startTime: null, endTime: null }]
				}
			})
			onChange({ target: { name, value: updatedSchedule } });
			return updatedSchedule;
		})
	}

	// This removes a from/until combo from the table. Except for if we only have one left.
	const handleRemoveExtraScheduleLine = () => {
		// loop over the days in the schedule and add another timeRange.
		setScheduleToUse(previousState => {
			const updatedSchedule = previousState.map(previousStateDay => {
				// Only remove if there's more than one timeRange
				if (previousStateDay.timeRanges.length > 1) {
					return {
						...previousStateDay,
						timeRanges: previousStateDay.timeRanges.slice(0, -1), // Remove last timeRange
					}
				}
				return previousStateDay;
			})
			onChange({ target: { name, value: updatedSchedule } });
			return updatedSchedule;
		})
	}

	const showTimePickers = () => {
		let startTime = scheduleToUse[selectedDay]?.timeRanges[selectedRange]?.startTime;
		if (startTime) {
			const [hours, minutes] = startTime.split(':');
			const date = new Date();
			date.setHours(hours);
			date.setMinutes(minutes);
			startTime = date;
		}
		let endTime = scheduleToUse[selectedDay]?.timeRanges[selectedRange]?.endTime;
		if (endTime) {
			const [hours, minutes] = endTime.split(':');
			const date = new Date();
			date.setHours(hours);
			date.setMinutes(minutes);
			endTime = date;
		}

		return (
			<>
				<TimePicker
					label="Start Time"
					value={startTime}
					onChange={(time) => handleTimeChange(time, 'startTime')}
					renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
					ampm={false}
					sx={{ 'marginRight': '24px' }}
				/>
				<TimePicker
					label="End Time"
					value={endTime}
					onChange={(time) => handleTimeChange(time, 'endTime')}
					renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
					ampm={false}
				/>
			</>
		);
	};

	// Get max number of time ranges to determine number of rows multiplied by 2 because we have a from and until row
	const maxTimeRanges = Math.max(...scheduleToUse.map(day => day.timeRanges.length));

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<TableContainer component={Paper} elevation={0}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Day</TableCell>
							{daysOfWeek.map(day => (
								<TableCell key={day} align="center">{day}</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{Array.from({ length: maxTimeRanges }).map((_, rangeIndex) => (
							<>
								<TableRow>
									<TableCell>From</TableCell>
									{daysOfWeek.map((day, dayIndex) => {
										const timeRange = scheduleToUse[dayIndex].timeRanges[rangeIndex];
										return (
											<TableCell
												key={day}
												align="center"
												onClick={() => handleOpenDialog(dayIndex, rangeIndex)}
												style={{ cursor: 'pointer' }}
											>
												{timeRange?.startTime || '-'}
											</TableCell>
										)
									})}
								</TableRow>
								<TableRow>
									<TableCell>Until</TableCell>
									{daysOfWeek.map((day, dayIndex) => {
										const timeRange = scheduleToUse[dayIndex].timeRanges[rangeIndex];
										return (
											<TableCell
												key={day}
												align="center"
												onClick={() => handleOpenDialog(dayIndex, rangeIndex)}
												style={{ cursor: 'pointer' }}
											>
												{timeRange?.endTime || '-'}
											</TableCell>
										)
									})}
								</TableRow>
							</>
						))}
					</TableBody>
				</Table>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end', marginTop: 2 }}>
					<Button onClick={handleAddExtraScheduleLine} variant='outlined' startIcon={<AddIcon />} sx={{ textTransform: 'none'}}>
						Add extra schedule
					</Button>
					<Button onClick={handleRemoveExtraScheduleLine} variant='outlined' color='error' startIcon={<RemoveIcon />} disabled={maxTimeRanges === 1} sx={{ textTransform: 'none'}}>
						Remove extra schedule
					</Button>
				</Box>
			</TableContainer>

			<Dialog open={openDialog} onClose={handleCloseDialog}>
				<DialogTitle>Set Time for {scheduleToUse[selectedDay]?.day}</DialogTitle>
				<DialogContent sx={{ paddingTop: '10px !important' }}>
					{selectedDay !== null && showTimePickers()}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog} color="primary" variant='outlined' sx={{ textTransform: 'none'}}>
						Confirm
					</Button>
				</DialogActions>
			</Dialog>
		</LocalizationProvider>
	);
};

Schedule.type = 'Schedule';

export default Schedule;
